import { LocalizationPipe } from '@abp/ng.core';
import { ToasterService } from '@abp/ng.theme.shared';
import { Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ZendraLocalizationServices, ZnButton } from '@zendra/shared';
import { StandPlanningOrchestratorService } from 'orchestrator';
import { finalize } from 'rxjs';

const GIS_CONNECTION_ERROR_KEY = `${ZendraLocalizationServices.Orchestrator}::App:GisConnectionError`;
const STAND_NOT_FOUND_IN_GIS_ERROR_KEY = `${ZendraLocalizationServices.Orchestrator}::App:StandNotFoundInGis`;

@Component({
  selector: 'lib-stand-gis',
  imports: [ZnButton, LocalizationPipe],
  templateUrl: './stand-gis.html',
  styleUrl: './stand-gis.scss',
})
export class StandGis {
  canFetchGisArea = input(false);
  name = input('');
  updatedGisArea = output<number>();

  localizationResource = ZendraLocalizationServices.LanguageService;

  gisAreaStatus = signal<'default' | 'success' | 'error'>('default');
  gisAreaLoading = signal(false);
  gisAreaLabel = signal(`${this.localizationResource}::Stand.GetGISArea`);
  gisAreaDefault = signal(`${this.localizationResource}::Stand.GetGISArea`);
  gisAreaSuccess = `${this.localizationResource}::Stand.GetGISAreaSuccess`;
  gisAreaError = `${this.localizationResource}::Stand.GetGISAreaError`;

  private readonly standService = inject(StandPlanningOrchestratorService);
  private readonly toaster = inject(ToasterService);
  private readonly destroyRef = inject(DestroyRef);

  fetchGisArea() {
    if (!this.canFetchGisArea()) return;

    this.gisAreaLoading.set(true);

    this.standService
      .getGisAreaByName(this.name())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.gisAreaLoading.set(false);
          setTimeout(() => {
            this.gisAreaLabel.set(this.gisAreaDefault());
            this.gisAreaStatus.set('default');
          }, 3000);
        }),
      )
      .subscribe({
        next: result => {
          this.gisAreaLabel.set(this.gisAreaSuccess);
          this.gisAreaStatus.set('success');
          this.updatedGisArea.emit(result);
        },
        error: error => {
          this.gisAreaLabel.set(this.gisAreaError);
          this.gisAreaStatus.set('error');
          if (this.shouldShowLocalToast(error)) {
            this.toaster.error(this.getGisErrorMessage(error), '', { life: 5000 });
          }
        },
      });
  }

  private getGisErrorMessage(error: { error?: { error?: { code?: string }; code?: string } }): string {
    const errorCode = error?.error?.error?.code ?? error?.error?.code;

    return errorCode === 'App:StandNotFoundInGis'
      ? STAND_NOT_FOUND_IN_GIS_ERROR_KEY
      : GIS_CONNECTION_ERROR_KEY;
  }

  private shouldShowLocalToast(error: { error?: { error?: { code?: string }; code?: string } }): boolean {
    const errorCode = error?.error?.error?.code ?? error?.error?.code;
    return errorCode !== 'App:StandNotFoundInGis';
  }
}
